/**
 * All sample data for Party "trial" flows (frontend-only, no API).
 * Content is Arabic-first (عربي) — English fields are fallbacks when UI language is English.
 * Edit this file to change trial content across games.
 */

import type {
  ModeDareCardRow,
  ModeGuessContentRow,
  ModeQuickQuestionRow,
} from "../types/api";

/** Shell ids that must not offer trial (e.g. real-time / WebSocket modes). */
export const PARTY_TRIAL_EXCLUDED_SHELL_IDS = ["bluff", "imposter-online"] as const;

export function partyShellSupportsTrial(shellId: string | undefined): boolean {
  if (!shellId) return false;
  return !(PARTY_TRIAL_EXCLUDED_SHELL_IDS as readonly string[]).includes(
    shellId,
  );
}

// --- Category (topic shown after rules) --------------------------------------

export const TRIAL_CATEGORY_TOPIC = {
  name_ar: "فواكه ووجبات خفيفة",
  name_en: "Fruit & snacks",
};

// --- Guess it (merged A/B deck order matches live API merge) ----------------

const TRIAL_GUESS_TEAM_A: ModeGuessContentRow[] = [
  {
    id: 91001,
    word: "رقي(بطيخ)",
    forbidden_words: ["أحمر", "صيف", "بذور"],
    sort_order: 1,
  },
  {
    id: 91002,
    word: "فشار",
    forbidden_words: ["سينما", "زبدة", "قرمشة"],
    sort_order: 2,
  },
  {
    id: 91003,
    word: "ساندويتش",
    forbidden_words: ["خبز", "غداء", "طبقات"],
    sort_order: 3,
  },
];

const TRIAL_GUESS_TEAM_B: ModeGuessContentRow[] = [
  {
    id: 92001,
    word: "حمص",
    forbidden_words: ["حبّة", "غموس", "مقبلات"],
    sort_order: 1,
  },
  {
    id: 92002,
    word: "تمر",
    forbidden_words: ["رمضان", "حلو", "نخلة"],
    sort_order: 2,
  },
  {
    id: 92003,
    word: "آيس كريم",
    forbidden_words: ["بارد", "ويفر", "ملعقة"],
    sort_order: 3,
  },
];

export function getTrialGuessMergedDeck(): ModeGuessContentRow[] {
  const merged: ModeGuessContentRow[] = [];
  const n = Math.max(TRIAL_GUESS_TEAM_A.length, TRIAL_GUESS_TEAM_B.length);
  for (let i = 0; i < n; i++) {
    const a = TRIAL_GUESS_TEAM_A[i];
    const b = TRIAL_GUESS_TEAM_B[i];
    if (a) merged.push(a);
    if (b) merged.push(b);
  }
  return merged;
}

// --- Dare / challenge deck ---------------------------------------------------

export const TRIAL_DARE_DECK: ModeDareCardRow[] = [
  {
    text_ar: "قلّد صوت مُعلِن أفلام لمدة عشر ثوانٍ.",
    text_en: "Movie-trailer voice for 10 seconds.",
    points: 2,
  },
  {
    text_ar: "اذكر خمس دول في أفريقيا خلال خمس عشرة ثانية دون تكرار.",
    text_en: "Five African countries in 15s, no repeats.",
    points: 3,
  },
  {
    text_ar: "احكِ قصة من جملتين تبدأ بـ «ذات يوم اثنين…».",
    text_en: "Two-sentence story starting “One Monday…”",
    points: 2,
  },
  {
    text_ar: "بدّل المكان مع اللاعب الآخر للبطاقة التالية إن أمكن.",
    text_en: "Swap seats with the other player for the next card.",
    points: 1,
  },
];

// --- Acting (interleaved teams like API merge) -----------------------------

export const TRIAL_ACTING_DECK: { team: "a" | "b"; word: string }[] = [
  { team: "a", word: "بطريق" },
  { team: "b", word: "بركان" },
  { team: "a", word: "كيكة عيد ميلاد" },
  { team: "b", word: "ازدحام مروري" },
  { team: "a", word: "مسّاح زجاج" },
  { team: "b", word: "صباغ" },
];

// --- Quick questions (merged A/B like API) ---------------------------------

const TRIAL_QUICK_A: ModeQuickQuestionRow[] = [
  {
    id: 93001,
    question: "اذكر ثلاثة أشياء تضعها في حقيبة السفر.",
    answer: "ملابس، شاحن، جواز سفر…",
    sort_order: 1,
  },
  {
    id: 93002,
    question: "أيهما أعلى صوتًا عادةً: جمهور ملعب ولا خلاط مطبخ؟",
    answer: "غالبًا جمهور الملعب.",
    sort_order: 2,
  },
];

const TRIAL_QUICK_B: ModeQuickQuestionRow[] = [
  {
    id: 94001,
    question: "ما عاصمة المملكة العربية السعودية؟",
    answer: "الرياض.",
    sort_order: 1,
  },
  {
    id: 94002,
    question: "أيهما أسرع تحضيرًا عادةً: كوب شاي ولا ساندويتش بسيط؟",
    answer: "غالبًا الشاي؛ يعتمد على الساندويتش.",
    sort_order: 2,
  },
];

export function getTrialQuickQuestionsDeck(): ModeQuickQuestionRow[] {
  const out: ModeQuickQuestionRow[] = [];
  const n = Math.max(TRIAL_QUICK_A.length, TRIAL_QUICK_B.length);
  for (let i = 0; i < n; i++) {
    if (TRIAL_QUICK_A[i]) out.push(TRIAL_QUICK_A[i]);
    if (TRIAL_QUICK_B[i]) out.push(TRIAL_QUICK_B[i]);
  }
  return out;
}

// --- Who am I (flat word list → split like postWhoAmIStart) ------------------

const TRIAL_WHO_AM_I_WORDS: string[] = [
  "رائد فضاء",
  "طباخ",
  "محقق",
  "محامي",
  "مهندس كمبيوتر",
  "طيار",
];

export function getTrialWhoAmIWordsSplit(): {
  wordsA: string[];
  wordsB: string[];
} {
  const half = Math.ceil(TRIAL_WHO_AM_I_WORDS.length / 2);
  return {
    wordsA: TRIAL_WHO_AM_I_WORDS.slice(0, half),
    wordsB: TRIAL_WHO_AM_I_WORDS.slice(half),
  };
}

// --- Imposter secret word (after lobby shuffle, same reveal flow) ------------

export const TRIAL_IMPOSTER_SECRET_WORD = "شواء ليلي";

/** Optional hints shown with the trial secret word (cast URL + reveal card). */
export const TRIAL_IMPOSTER_HINTS: string[] = ["نار", "ليل", "رائحة لحم"];
